import { motion, useReducedMotion } from 'framer-motion';
import { Lock } from 'lucide-react';
import { Eyebrow, RaceBib } from '@/components/foundry';

// ─────────────────────────────────────────────────────────────────────────
// Post-it module modules (variation A) — workbook proposal preview.
//
// Spec: per Tom 2026-05-07 — module cards reimagined as post-it notes.
// Canary paper, washi-tape across the top, irregular per-card tilts, the
// existing BuildRoom module character covers loaded as the central visual,
// race-bib stamp counter-rotated, rubber-stamp state pill.
//
// This is a preview surface only — lives in foundry-preview/composites.
// Production integration happens later in a dedicated Phase 4 sprint.
// ─────────────────────────────────────────────────────────────────────────

type ModuleNum = '00' | '01' | '02' | '03' | '04' | '05' | '06' | '07' | '08';

interface PostItSpec {
  num: ModuleNum;
  name: string;
  highlight: string;
  cover: string;
  cardTilt: number;
  tapeTilt: number;
  bibTilt: number;
  state: 'ready' | 'locked' | 'paused';
  tapeTone: 'teal' | 'coral' | 'ink';
  /** Texture pad color — index into POSTIT_TEXTURES (01 canary, 02 coral, 03 mint, 04 sky, 05 lavender). */
  textureKey: '01' | '02' | '03' | '04' | '05';
  /** Optional Caveat margin annotation — only on a few cards so it stays annotation, not decoration. */
  scribble?: { text: string; tone: 'ink' | 'teal' | 'coral'; rotate: number };
}

// 9-module grid mirrors BuildRoom WORKSHOPS data + Tom's approved gold-standard layout.
// Tilts vary irregularly so the grid feels hand-pinned, not algorithmic. Texture keys
// cycle 5 paper colors with canary dominant (3×) and accents at 2×/2×/1×/1×. Scribbles
// only on 4 of 9 cards — annotation, not decoration.
const POSTITS: PostItSpec[] = [
  {
    num: '00',
    name: 'The Shift',
    highlight: 'Shift',
    cover: '/character-system/scenes/build-room-module-covers-v2/build-room-module-00-the-shift-v1.png',
    cardTilt: -1.6,
    tapeTilt: 1.2,
    bibTilt: -3.5,
    state: 'ready',
    tapeTone: 'teal',
    textureKey: '01',
    scribble: { text: '← start here', tone: 'coral', rotate: -4 },
  },
  {
    num: '01',
    name: 'Your AI Acquisition Operator',
    highlight: 'Operator',
    cover: '/character-system/scenes/build-room-module-covers-v2/build-room-module-01-acquisition-operator-v1.png',
    cardTilt: 0.8,
    tapeTilt: -1.0,
    bibTilt: 4,
    state: 'locked',
    tapeTone: 'coral',
    textureKey: '03',
  },
  {
    num: '02',
    name: 'Route Before Asset',
    highlight: 'Route',
    cover: '/character-system/scenes/build-room-module-covers-v2/build-room-module-02-route-before-asset-v1.png',
    cardTilt: 2.4,
    tapeTilt: 0.4,
    bibTilt: -5,
    state: 'locked',
    tapeTone: 'ink',
    textureKey: '02',
  },
  {
    num: '03',
    name: 'Acquisition Radar',
    highlight: 'Radar',
    cover: '/character-system/scenes/build-room-module-covers-v2/build-room-module-03-acquisition-radar-v1.png',
    cardTilt: -2.0,
    tapeTilt: 1.6,
    bibTilt: 3.5,
    state: 'locked',
    tapeTone: 'teal',
    textureKey: '04',
  },
  {
    num: '04',
    name: 'Creative Intelligence',
    highlight: 'Creative',
    cover: '/character-system/scenes/build-room-module-covers-v2/build-room-module-04-creative-intelligence-v1.png',
    cardTilt: 1.4,
    tapeTilt: -0.8,
    bibTilt: -4,
    state: 'locked',
    tapeTone: 'coral',
    textureKey: '01',
    scribble: { text: 'fun bit', tone: 'coral', rotate: 5 },
  },
  {
    num: '05',
    name: 'Campaign Proposal',
    highlight: 'Campaign',
    cover: '/character-system/scenes/build-room-module-covers-v2/build-room-module-05-campaign-proposal-v1.png',
    cardTilt: -0.8,
    tapeTilt: 2.2,
    bibTilt: 4.5,
    state: 'paused',
    tapeTone: 'ink',
    textureKey: '03',
    scribble: { text: 'tom reviews this', tone: 'teal', rotate: 3 },
  },
  {
    num: '06',
    name: 'Human-Approved Launch',
    highlight: 'Launch',
    cover: '/character-system/scenes/build-room-module-covers-v2/build-room-module-06-human-approved-launch-v1.png',
    cardTilt: 2.0,
    tapeTilt: -1.4,
    bibTilt: -3,
    state: 'locked',
    tapeTone: 'teal',
    textureKey: '02',
  },
  {
    num: '07',
    name: 'Signal Loop + Leak Review',
    highlight: 'Signal',
    cover: '/character-system/scenes/build-room-module-covers-v2/build-room-module-07-signal-loop-v1.png',
    cardTilt: -1.2,
    tapeTilt: 0.8,
    bibTilt: 4,
    state: 'locked',
    tapeTone: 'coral',
    textureKey: '05',
  },
  {
    num: '08',
    name: 'Operating With The Lab',
    highlight: 'Lab',
    cover: '/character-system/scenes/build-room-module-covers-v2/build-room-module-08-operating-with-the-lab-v1.png',
    cardTilt: 1.8,
    tapeTilt: -2.0,
    bibTilt: -4.5,
    state: 'locked',
    tapeTone: 'ink',
    textureKey: '01',
    scribble: { text: 'next.', tone: 'ink', rotate: -6 },
  },
];

// Texture rotation table — file paths Codex generates into. Until the textures land,
// PostItCard falls back to its procedural CSS canary background regardless of textureKey.
// Once the PNGs exist on disk, swap the gradient for `background-image: url(POSTIT_TEXTURES[k])`.
const POSTIT_TEXTURES = {
  '01': '/character-system/scenes/post-it-textures-v1/post-it-01.png',
  '02': '/character-system/scenes/post-it-textures-v1/post-it-02.png',
  '03': '/character-system/scenes/post-it-textures-v1/post-it-03.png',
  '04': '/character-system/scenes/post-it-textures-v1/post-it-04.png',
  '05': '/character-system/scenes/post-it-textures-v1/post-it-05.png',
} as const;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _texturesReady = POSTIT_TEXTURES; // Reserved for texture-rotation swap once PNGs land.
void _texturesReady;

export default function PostItModulesPreview() {
  return (
    <section className="grid gap-6">
      <header className="flex items-end justify-between gap-6 border-b border-fd-border pb-4">
        <div>
          <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <Eyebrow>Workbook · Variation A · Post-it on grid paper</Eyebrow>
            <span
              className="font-caveat text-[18px] font-medium text-fd-coral"
              style={{ transform: 'rotate(-2deg)', display: 'inline-block' }}
            >
              just slap it on.
            </span>
          </div>
          <h2 className="font-geist text-[22px] font-semibold leading-[1.1] tracking-[-0.02em] text-fd-text">
            Module cards reimagined as{' '}
            <span
              className="font-caveat text-[34px] font-medium text-fd-text"
              style={{ transform: 'rotate(-2deg)', display: 'inline-block' }}
            >
              post-its.
            </span>
          </h2>
          <p className="mt-1.5 max-w-[640px] text-[13px] leading-[1.55] text-fd-text-muted">
            Canary paper, washi tape across the top, irregular tilts, existing module covers as the
            central visual. Hover to see the card settle flat.
          </p>
        </div>
        <span className="hidden shrink-0 font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle sm:inline">
          9 modules · 5 paper colors · textures pending
        </span>
      </header>

      {/* Notebook background — ruled paper grid behind the post-its */}
      <div
        className="relative isolate overflow-hidden rounded-[14px] border border-fd-border bg-fd-bg p-10"
        style={{
          backgroundImage:
            'linear-gradient(to bottom, transparent 0, transparent 31px, oklch(0.86 0.01 95 / 0.55) 31px, oklch(0.86 0.01 95 / 0.55) 32px, transparent 32px), linear-gradient(to right, transparent 0, transparent 31px, oklch(0.86 0.01 95 / 0.40) 31px, oklch(0.86 0.01 95 / 0.40) 32px, transparent 32px)',
          backgroundSize: '32px 32px',
        }}
      >
        {/* Margin pin-prick texture */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-6 left-8 hidden w-px bg-fd-coral/30 lg:block"
          style={{ boxShadow: '0 0 0 0.5px oklch(0.62 0.20 25 / 0.18)' }}
        />

        <div className="grid grid-cols-1 gap-y-12 sm:grid-cols-2 sm:gap-x-8 lg:grid-cols-3 lg:gap-x-6">
          {POSTITS.map((spec) => (
            <PostItCard key={spec.num} spec={spec} />
          ))}
        </div>
      </div>

      {/* Spec annotations */}
      <details className="group rounded-[10px] border border-fd-border bg-fd-paper px-5 py-4">
        <summary className="cursor-pointer list-none font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-fd-text-subtle transition-colors hover:text-fd-text">
          Variation A spec — what's on each card
          <span className="ml-2 transition-transform group-open:rotate-90">›</span>
        </summary>
        <div className="mt-3 grid gap-3 text-[12.5px] leading-[1.55] text-fd-text-muted sm:grid-cols-2">
          <div>
            <p className="mb-1 font-geist text-[12px] font-semibold uppercase tracking-[0.06em] text-fd-text">Paper</p>
            <p>Canary yellow (warm, not race-bib gold). Subtle paper grain. Slight inner shadow on right edge for thickness.</p>
          </div>
          <div>
            <p className="mb-1 font-geist text-[12px] font-semibold uppercase tracking-[0.06em] text-fd-text">Washi tape</p>
            <p>Muted teal / coral / ink across the top. Independent tilt from card. Slight serrated edges.</p>
          </div>
          <div>
            <p className="mb-1 font-geist text-[12px] font-semibold uppercase tracking-[0.06em] text-fd-text">Race-bib stamp</p>
            <p>Top-right corner, counter-rotated to break the card tilt. Existing RaceBib primitive.</p>
          </div>
          <div>
            <p className="mb-1 font-geist text-[12px] font-semibold uppercase tracking-[0.06em] text-fd-text">Module cover</p>
            <p>The existing BuildRoom module images — no new generation needed. Centered, with paper-multiplied integration.</p>
          </div>
          <div>
            <p className="mb-1 font-geist text-[12px] font-semibold uppercase tracking-[0.06em] text-fd-text">Title + highlight</p>
            <p>Geist medium with a teal marker underline on the key word. Body stays plain.</p>
          </div>
          <div>
            <p className="mb-1 font-geist text-[12px] font-semibold uppercase tracking-[0.06em] text-fd-text">State stamp</p>
            <p>Bottom-right, rubber-stamp ink-on-yellow feel. READY / LOCKED / PAUSED — slight rotation.</p>
          </div>
          <div>
            <p className="mb-1 font-geist text-[12px] font-semibold uppercase tracking-[0.06em] text-fd-text">Hover</p>
            <p>Card settles toward 0° tilt, lifts 6px, shadow deepens. Spring physics — no linear easing.</p>
          </div>
          <div>
            <p className="mb-1 font-geist text-[12px] font-semibold uppercase tracking-[0.06em] text-fd-text">Mobile</p>
            <p>Single column, tilts preserved (the irregularity is the point). Cards stack with generous gap.</p>
          </div>
        </div>
      </details>
    </section>
  );
}

// ── Post-it card component ─────────────────────────────────────────────

function PostItCard({ spec }: { spec: PostItSpec }) {
  const prefersReducedMotion = useReducedMotion();
  const reduced = Boolean(prefersReducedMotion);

  const tapeColor = TAPE_COLORS[spec.tapeTone];

  return (
    <motion.div
      className="relative mx-auto"
      style={{
        rotate: spec.cardTilt,
        transformOrigin: 'center center',
      }}
      whileHover={
        reduced
          ? undefined
          : { rotate: 0, y: -6, scale: 1.015, transition: { type: 'spring', stiffness: 220, damping: 22 } }
      }
    >
      {/* Card body */}
      <div
        className="relative w-[280px] sm:w-[300px]"
        style={{
          aspectRatio: '4 / 5',
          background:
            'radial-gradient(120% 110% at 30% 0%, oklch(0.96 0.10 92) 0%, oklch(0.92 0.13 92) 60%, oklch(0.88 0.13 92) 100%)',
          boxShadow:
            '0 1px 0 0 oklch(0.85 0.12 92) inset, ' +
            '-1px 0 0 0 oklch(0.84 0.10 90) inset, ' +
            '14px 18px 28px -14px oklch(0.30 0.04 90 / 0.42), ' +
            '4px 6px 10px -6px oklch(0.30 0.04 90 / 0.30)',
        }}
      >
        {/* Paper grain texture (SVG noise filter) */}
        <svg
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.16] mix-blend-multiply"
        >
          <filter id={`grain-${spec.num}`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" seed={Number(spec.num) + 3} />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.18  0 0 0 0 0.14  0 0 0 0 0.10  0 0 0 0.55 0"
            />
          </filter>
          <rect width="100%" height="100%" filter={`url(#grain-${spec.num})`} />
        </svg>

        {/* Race-bib stamp — top-right, counter-rotated */}
        <span
          className="absolute right-3 top-3 z-10"
          style={{ transform: `rotate(${spec.bibTilt}deg)`, transformOrigin: 'center center' }}
        >
          <RaceBib n={spec.num} tone="race" size="md" shadow="sm" />
        </span>

        {/* Module cover image — central focal point */}
        <div className="relative flex h-[58%] items-center justify-center px-6 pt-12">
          <img
            src={spec.cover}
            alt=""
            className="h-full w-auto max-w-full object-contain"
            style={{ filter: 'drop-shadow(0 8px 14px oklch(0.30 0.04 90 / 0.22))' }}
            loading="lazy"
          />
        </div>

        {/* Title block — bottom 42% */}
        <div className="absolute inset-x-0 bottom-0 flex h-[42%] flex-col justify-end gap-2 px-5 pb-5">
          <h3 className="font-geist text-[17px] font-medium leading-[1.18] tracking-[-0.015em] text-fd-text [text-wrap:balance]">
            {renderTitleWithHighlight(spec.name, spec.highlight)}
          </h3>
          <div className="flex items-end justify-between gap-3">
            <span
              className="font-caveat text-[16px] font-medium leading-none text-fd-text-muted"
              style={{ transform: 'rotate(-1.5deg)', display: 'inline-block' }}
            >
              module {spec.num}
            </span>
            <CaveatStateStamp state={spec.state} />
          </div>
        </div>

        {/* Caveat margin scribble — peek of personality off the card edge (only on a few cards) */}
        {spec.scribble ? (
          <span
            aria-hidden
            className="pointer-events-none absolute z-10 font-caveat text-[15px] font-medium whitespace-nowrap"
            style={{
              bottom: '8px',
              right: '-44px',
              color: SCRIBBLE_COLORS[spec.scribble.tone],
              transform: `rotate(${spec.scribble.rotate}deg)`,
              transformOrigin: 'left center',
            }}
          >
            {spec.scribble.text}
          </span>
        ) : null}

        {/* Right-edge paper-thickness highlight */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-2 right-0 w-px"
          style={{ background: 'linear-gradient(to bottom, transparent, oklch(0.78 0.10 90 / 0.38) 18%, oklch(0.78 0.10 90 / 0.38) 82%, transparent)' }}
        />
      </div>

      {/* Washi tape — pinned across top */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-0 z-20 h-6 w-[110px] sm:w-[124px]"
        style={{
          transform: `translate(-50%, -55%) rotate(${spec.tapeTilt}deg)`,
          background: tapeColor.bg,
          maskImage:
            'linear-gradient(to right, transparent 0, black 6%, black 94%, transparent 100%), linear-gradient(to bottom, transparent 0, black 8%, black 92%, transparent 100%)',
          maskComposite: 'intersect',
          WebkitMaskImage:
            'linear-gradient(to right, transparent 0, black 6%, black 94%, transparent 100%), linear-gradient(to bottom, transparent 0, black 8%, black 92%, transparent 100%)',
          WebkitMaskComposite: 'source-in',
          boxShadow:
            '0 1px 0 0 oklch(1 0 0 / 0.18) inset, ' +
            `0 5px 8px -4px ${tapeColor.shadow}`,
        }}
      >
        {/* Tape grain stripes */}
        <span
          className="absolute inset-0 opacity-[0.32] mix-blend-multiply"
          style={{
            backgroundImage:
              'repeating-linear-gradient(115deg, oklch(0.40 0.04 95 / 0.12) 0 2px, transparent 2px 6px)',
          }}
        />
      </span>
    </motion.div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────

const TAPE_COLORS = {
  teal: { bg: 'oklch(0.62 0.10 175 / 0.78)', shadow: 'oklch(0.40 0.06 175 / 0.30)' },
  coral: { bg: 'oklch(0.66 0.18 25 / 0.72)', shadow: 'oklch(0.40 0.10 25 / 0.28)' },
  ink: { bg: 'oklch(0.30 0.04 90 / 0.62)', shadow: 'oklch(0.20 0.02 90 / 0.40)' },
} as const;

function renderTitleWithHighlight(name: string, highlight: string): React.ReactNode {
  if (!highlight) return name;
  const idx = name.indexOf(highlight);
  if (idx < 0) return name;
  const before = name.slice(0, idx);
  const after = name.slice(idx + highlight.length);
  return (
    <>
      {before}
      <span className="relative inline-block">
        <span className="relative z-10">{highlight}</span>
        <span
          aria-hidden
          className="absolute inset-x-0 bottom-[3px] h-[6px] -z-0"
          style={{ background: 'oklch(0.62 0.10 175 / 0.40)' }}
        />
      </span>
      {after}
    </>
  );
}

function CaveatStateStamp({ state }: { state: PostItSpec['state'] }) {
  const config = STATE_CONFIG[state];

  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 font-caveat text-[20px] font-medium leading-none"
      style={{
        transform: `rotate(${config.tilt}deg)`,
        color: config.color,
      }}
    >
      {state === 'locked' && <Lock className="h-3 w-3" strokeWidth={2.4} />}
      {config.label}
    </span>
  );
}

const STATE_CONFIG = {
  ready: {
    label: 'ready',
    color: 'oklch(0.45 0.13 145)',
    tilt: -4,
  },
  paused: {
    label: 'paused',
    color: 'oklch(0.50 0.18 35)',
    tilt: 3,
  },
  locked: {
    label: 'locked',
    color: 'oklch(0.34 0.02 95)',
    tilt: -3,
  },
} as const;

const SCRIBBLE_COLORS = {
  ink: 'oklch(0.30 0.02 95)',
  teal: 'oklch(0.50 0.10 175)',
  coral: 'oklch(0.55 0.18 30)',
} as const;
